import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Lenis from 'lenis';
import { ArrowLeft } from 'lucide-react';
import { MonkeyLogo } from '../components/MonkeyLogo';

const DISPLAY = "font-['Cabinet_Grotesk',_'Inter',_sans-serif] font-black tracking-tighter";
const BODY = "font-['Satoshi',_'Inter',_sans-serif]";
const EASE_OUT = [0.16, 1, 0.3, 1];
const EFFECTIVE = 'February 21, 2026';

const SECTIONS = [
  {
    h: '1. Our position on copyright',
    p: `LinkMonkey respects intellectual property rights and complies with the U.S. Digital Millennium Copyright Act (DMCA) and the EU Copyright Directive. Although we are an EU‑based service, we follow the DMCA notice‑and‑takedown procedure as a global best practice. If you are a copyright owner (or an authorised agent) and you believe that content hosted on or linked to from LinkMonkey infringes your rights, you can submit a takedown notice using the procedure below.`,
  },
  {
    h: '2. How to submit a takedown notice',
    children: (
      <div className="space-y-4">
        <p className={`${BODY} text-white/65 leading-relaxed text-[15px] md:text-base`}>
          Email <a href="mailto:dmca@linkmonkey.io" className="text-white/85 underline underline-offset-4">dmca@linkmonkey.io</a> with the subject line <em>"DMCA Takedown Notice"</em> and include all of the following — incomplete notices cannot be processed:
        </p>
        <ul className="list-disc list-outside ml-5 space-y-2.5 text-white/65 leading-relaxed text-[15px] md:text-base">
          <li>A description of the copyrighted work you claim has been infringed (or a representative list, if multiple works are at issue).</li>
          <li>The exact URL(s) on LinkMonkey where the allegedly infringing content appears (e.g. <code className="text-white/85 font-mono text-[13px]">https://linkmonkey.io/&lt;username&gt;</code>).</li>
          <li>Your full legal name, postal address, phone number and email.</li>
          <li>A statement that you have a good‑faith belief that the use is not authorised by the copyright owner, its agent, or the law.</li>
          <li>A statement, under penalty of perjury, that the information in the notice is accurate and that you are the copyright owner or authorised to act on the owner's behalf.</li>
          <li>Your physical or electronic signature.</li>
        </ul>
      </div>
    ),
  },
  {
    h: '3. What we do with it',
    p: `On receipt of a complete and good‑faith notice we will, where appropriate, (a) disable access to the identified content within 1–3 business days, (b) notify the user who uploaded or linked to the content and forward them the notice, and (c) maintain a record of the action in our admin audit log. Repeat infringement may result in account suspension or termination under our Terms of Service.`,
  },
  {
    h: '4. Counter‑notice',
    children: (
      <div className="space-y-4">
        <p className={`${BODY} text-white/65 leading-relaxed text-[15px] md:text-base`}>
          If you believe your content was removed by mistake or as a result of misidentification, you may file a counter‑notice. Email <a href="mailto:dmca@linkmonkey.io" className="text-white/85 underline underline-offset-4">dmca@linkmonkey.io</a> with subject line <em>"DMCA Counter‑Notice"</em> and include:
        </p>
        <ul className="list-disc list-outside ml-5 space-y-2.5 text-white/65 leading-relaxed text-[15px] md:text-base">
          <li>Identification of the content that was removed and the URL where it appeared before removal.</li>
          <li>Your full legal name, address, phone number and email.</li>
          <li>A statement under penalty of perjury that you have a good‑faith belief the content was removed as a result of mistake or misidentification.</li>
          <li>A statement that you consent to the jurisdiction of the courts in Dublin, Ireland (or, if you are outside the EU, the U.S. federal district court for the district in which you reside).</li>
          <li>Your physical or electronic signature.</li>
        </ul>
        <p className={`${BODY} text-white/65 leading-relaxed text-[15px] md:text-base`}>
          We will forward your counter‑notice to the original complainant. Unless they file a lawsuit within 10–14 business days, we may restore the content.
        </p>
      </div>
    ),
  },
  {
    h: '5. False claims',
    p: `Knowingly submitting a false DMCA notice or counter‑notice may make you liable for damages, including costs and attorneys' fees, under 17 U.S.C. § 512(f). Don't do it.`,
  },
  {
    h: '6. Repeat infringers',
    p: `In accordance with the DMCA and applicable EU law, we will terminate the accounts of users who are determined to be repeat infringers in appropriate circumstances.`,
  },
  {
    h: '7. Contact',
    p: `All DMCA correspondence: dmca@linkmonkey.io. General legal: legal@linkmonkey.io. We acknowledge receipt of every notice within 2 business days.`,
  },
];

export default function DMCA() {
  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.1 });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <div className="min-h-screen bg-[#06070A] text-white" data-testid="dmca-page">
      <Helmet>
        <title>DMCA & Copyright — LinkMonkey</title>
        <meta name="description" content="How to file a copyright takedown notice for content hosted on or linked to from LinkMonkey." />
      </Helmet>

      <div className="fixed inset-0 pointer-events-none opacity-[0.04] bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')]" />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[120vw] h-[60vh] pointer-events-none"
           style={{ background: 'radial-gradient(ellipse at top, rgba(0,85,255,0.18) 0%, transparent 60%)' }} />

      <nav className="relative z-10 px-6 md:px-10 py-6 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-3 group" data-testid="dmca-nav-home">
          <MonkeyLogo size={32} />
          <span className={`${BODY} text-base font-semibold text-white`}>LinkMonkey</span>
        </Link>
        <Link to="/" className={`${BODY} flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors`} data-testid="dmca-back-home">
          <ArrowLeft className="w-4 h-4" />
          Back home
        </Link>
      </nav>

      <header className="relative z-10 px-6 md:px-10 pt-12 md:pt-20 pb-10 max-w-3xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE_OUT }}
          className={`${BODY} text-[11px] uppercase tracking-[0.3em] text-white/40 mb-5`}>
          Legal
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.05 }}
          className={`${DISPLAY} text-5xl md:text-7xl leading-[0.95] mb-6`}>
          Copyright <em className="italic font-light text-white/80">& DMCA</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.1 }}
          className={`${BODY} text-white/55 text-base md:text-lg leading-relaxed max-w-2xl`}>
          How to file a takedown notice and what we'll do about it. Notice‑and‑takedown is the law — we follow it carefully.
        </motion.p>
        <p className={`${BODY} text-xs text-white/30 mt-6 font-mono`}>Effective {EFFECTIVE}</p>
      </header>

      <main className="relative z-10 px-6 md:px-10 pb-24 max-w-3xl mx-auto">
        <div className="space-y-10">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.h}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, ease: EASE_OUT, delay: Math.min(i * 0.02, 0.2) }}
              data-testid={`dmca-section-${i + 1}`}
            >
              <h2 className={`${DISPLAY} text-2xl md:text-3xl text-white mb-3`}>{s.h}</h2>
              {s.children ? s.children : (
                <p className={`${BODY} text-white/65 leading-relaxed text-[15px] md:text-base`}>{s.p}</p>
              )}
            </motion.section>
          ))}
        </div>

        <div className="mt-20 pt-10 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className={`${BODY} text-sm text-white/40`}>
            See also our{' '}
            <Link to="/terms" className="text-white/80 hover:text-white underline underline-offset-4">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-white/80 hover:text-white underline underline-offset-4">Privacy Policy</Link>.
          </p>
          <Link to="/" className={`${BODY} text-sm text-white/50 hover:text-white transition-colors`}>
            ← Back to LinkMonkey
          </Link>
        </div>
      </main>
    </div>
  );
}
